import { JitsiLocalTrack } from 'lib-jitsi-meet'

import { createAction } from 'redux-actions'

import { AppReduxStore, RootState } from '@tabeeb/store'
import { isLocalRecordingActive } from '@tabeeb/modules/recording/services/localRecordingService'
import { openSwitchRecordingSourceDialog } from '@tabeeb/modules/whiteboard/actions'
import { getContentId, getIsCurrentUserBeingRecorded } from '@tabeeb/shared/content/selectors'
import { signalrActions } from '@tabeeb/modules/signalr'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import { muteVideo } from '@tabeeb/modules/presentationToolbar/actions/muteVideo'

import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { AUDIO_DEVICE, DESKTOP_DEVICE, VIDEO_DEVICE } from '../constants'
import * as devicesActions from './devices'
import * as trackActions from './tracks'
import * as connectionService from '../services/connectionService'
import { AudioMixerEffect } from '../effects'
import { getIfCameraMutedBeforeScreenSharing, getIsVideoMuted } from '../selectors'

export const toggleScreenSharing = createAction<void>('TOGGLE_SCREEN_SHARING')
export const setScreenSharingError = createAction<string>('SET_SCREEN_SHARING_ERROR')
export const setScreenSharingState = createAction<boolean>('SET_SCREEN_SHARING_STATE')
export const setSystemAudioTrack = createAction<JitsiLocalTrack>('SET_SYSTEM_AUDIO_TRACK')
export const resetScreenSharingState = createAction<void>('RESET_SCREEN_SHARING_STATE')
export const setIfCameraMutedBeforeScreenSharing = createAction<boolean>('SET_IF_CAMERA_MUTED_BEFORE_SCREEN_SHARING')

export function stopScreenSharing(
  approvedByLocalRecording: boolean = false
): ThunkAction<void, RootState, unknown, AnyAction> {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: AppReduxStore['getState']) => {
    const isCurrentUserBeingRecorded = getIsCurrentUserBeingRecorded(getState())
    if (isLocalRecordingActive() && !approvedByLocalRecording && isCurrentUserBeingRecorded) {
      dispatch(openSwitchRecordingSourceDialog())
      return
    }

    const state = getState()
    const videoInputDeviceId = state.presentation.devices.videoInputId

    const { systemAudioTrack } = state.presentation.screenSharing
    if (systemAudioTrack) {
      const localTrack = state.presentation.tracks.localTracks.find((item) => item.getType() === AUDIO_DEVICE)
      if (localTrack) {
        localTrack.setEffect(undefined)
      }

      systemAudioTrack.dispose()
    }

    const wasCameraMutedBefore = getIfCameraMutedBeforeScreenSharing(state)
    dispatch(toggleScreenSharing())
    dispatch(
      devicesActions.setVideoInputDevice({
        deviceId: videoInputDeviceId,
        approvedByLocalRecording,
        muted: wasCameraMutedBefore,
      })
    )
    if (wasCameraMutedBefore) {
      dispatch(muteVideo())
    }

    const contentId = getContentId(state)
    const currentUserId = getCurrentUserId(state)
    dispatch(signalrActions.invokeHubAction({ method: 'OnScreenSharingStopped', args: [contentId, currentUserId] }))
  }
}

export function startScreenSharing(
  approvedByLocalRecording: boolean = false
): ThunkAction<void, RootState, unknown, AnyAction> {
  return (dispatch: ThunkDispatch<RootState, unknown, AnyAction>, getState: AppReduxStore['getState']) => {
    const state = getState()
    const isCurrentUserBeingRecorded = getIsCurrentUserBeingRecorded(state)
    if (isLocalRecordingActive() && !approvedByLocalRecording && isCurrentUserBeingRecorded) {
      dispatch(openSwitchRecordingSourceDialog())
      return
    }
    dispatch(setScreenSharingState(true))
    dispatch(toggleScreenSharing())

    const cameraMuted = getIsVideoMuted(state)
    dispatch(setIfCameraMutedBeforeScreenSharing(cameraMuted))

    connectionService
      .createLocalTracks({
        devices: [DESKTOP_DEVICE],
        resolution: `${window.config?.resolution ?? 720}`,
      })
      .then(
        (tracks) => {
          const videoTrack = tracks.find((track) => track.type === VIDEO_DEVICE)!
          if (!videoTrack) {
            throw new Error('Failed to share the screen: video track not found')
          }

          const systemAudioTrack = tracks.find((track) => track.type === AUDIO_DEVICE)
          if (systemAudioTrack) {
            dispatch(setSystemAudioTrack(systemAudioTrack))

            const localTrack = state.presentation.tracks.localTracks.find((item) => item.getType() === AUDIO_DEVICE)
            if (localTrack) {
              localTrack.setEffect(new AudioMixerEffect(systemAudioTrack))
            } else {
              dispatch(trackActions.replaceTrack({ track: systemAudioTrack }))
            }
          }

          videoTrack.track.onended = () => {
            dispatch(stopScreenSharing())
          }

          dispatch(trackActions.replaceTrack({ track: videoTrack, approvedByLocalRecording }))
          dispatch(setScreenSharingState(false))

          const contentId = getContentId(state)
          const currentUserId = getCurrentUserId(state)
          dispatch(
            signalrActions.invokeHubAction({ method: 'OnScreenSharingStarted', args: [contentId, currentUserId] })
          )
        },
        (error) => {
          dispatch(setScreenSharingError(error))
          dispatch(setScreenSharingState(false))
          dispatch(toggleScreenSharing())
        }
      )
  }
}
